import React from 'react';
import { CONTACT_INFO, COMPANY_INFO } from '../utils/constants';
import { SEO_KEYWORDS } from '../utils/seoKeywords';
import PageSEO from '../components/SEO';
import './About.css';

const About: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": `About ${COMPANY_INFO.name}`,
    "description": "Learn about our pickup truck rental company in Dubai, our mission, values, and commitment to providing excellent transportation services.",
    "url": `${COMPANY_INFO.website}/about`,
    "mainEntity": {
      "@type": "LocalBusiness",
      "name": COMPANY_INFO.name,
      "description": COMPANY_INFO.description,
      "telephone": CONTACT_INFO.phone,
      "email": CONTACT_INFO.email,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": CONTACT_INFO.address,
        "addressLocality": "Dubai",
        "addressCountry": "UAE"
      }
    }
  };

  return (
    <div className="about-page">
        <PageSEO
          title="About Us | Pickup Rental Truck Dubai – Trusted Company"
          description="About Pickup Rental Truck Dubai: trusted truck rental in Dubai. 1–10 ton trucks with tail lift for home shifting, office moving & delivery. 24/7."
          keywords={SEO_KEYWORDS.about}
        />
        {/* Hero Section */}
        <section className="about-hero">
          <div className="container">
            <h1>About Pickup Rental Truck Dubai</h1>
            <p>Your trusted partner for professional pickup truck rental services in Dubai</p>
          </div>
        </section>

        {/* Company Overview */}
        <section className="company-overview">
          <div className="container">
            <div className="overview-content">
              <div className="overview-text">
                <h2>Who We Are</h2>
                <p>
                  {COMPANY_INFO.name} is a leading pickup truck rental company based in Dubai, UAE. 
                  We specialize in providing reliable and professional transportation solutions for 
                  individuals and businesses across the emirate.
                </p>
                <p>
                  With years of experience in the transportation industry, we understand the unique 
                  needs of our clients and are committed to delivering exceptional service quality. 
                  Our fleet of well-maintained pickup trucks with tail lift capabilities ensures 
                  efficient and safe transportation of goods and materials.
                </p>
                <p>
                  Whether you need a truck for home shifting, office relocation, or delivery services, 
                  we have the right vehicle and professional team to meet your requirements.
                </p>
              </div>
              <div className="overview-image">
                <img src="/pickup-rental-truck-dubai-about.jpeg" alt="Our Fleet" />
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="mission-vision">
          <div className="container">
            <div className="mission-vision-grid">
              <div className="mission-card">
                <div className="card-icon">🎯</div>
                <h3>Our Mission</h3>
                <p>
                  To provide reliable, efficient, and affordable pickup truck rental services 
                  that exceed our customers' expectations while maintaining the highest standards 
                  of safety and professionalism.
                </p>
              </div>
              <div className="vision-card">
                <div className="card-icon">👁️</div>
                <h3>Our Vision</h3>
                <p>
                  To become the most trusted and preferred pickup truck rental company in Dubai, 
                  known for our exceptional service quality, modern fleet, and customer satisfaction.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="values">
          <div className="container">
            <h2>Our Core Values</h2>
            <div className="values-grid">
              <div className="value-item">
                <div className="value-icon">🛡️</div>
                <h4>Reliability</h4>
                <p>We deliver on our promises and ensure timely service delivery every time.</p>
              </div>
              <div className="value-item">
                <div className="value-icon">⭐</div>
                <h4>Quality</h4>
                <p>We maintain high standards in our vehicles, services, and customer interactions.</p>
              </div>
              <div className="value-item">
                <div className="value-icon">🤝</div>
                <h4>Trust</h4>
                <p>We build long-term relationships based on transparency and integrity.</p>
              </div>
              <div className="value-item">
                <div className="value-icon">💡</div>
                <h4>Innovation</h4>
                <p>We continuously improve our services and adopt modern transportation solutions.</p>
              </div>
              <div className="value-item">
                <div className="value-icon">❤️</div>
                <h4>Customer Focus</h4>
                <p>Our customers' satisfaction is at the heart of everything we do.</p>
              </div>
              <div className="value-item">
                <div className="value-icon">🌱</div>
                <h4>Sustainability</h4>
                <p>We are committed to environmentally responsible transportation practices.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="why-choose-us">
          <div className="container">
            <h2>Why Choose Our Services?</h2>
            <div className="advantages-grid">
              <div className="advantage-item">
                <h4>🏆 Experienced Team</h4>
                <p>Our professional drivers and support staff have extensive experience in the transportation industry.</p>
              </div>
              <div className="advantage-item">
                <h4>🚛 Modern Fleet</h4>
                <p>Well-maintained pickup trucks with tail lift for easy loading and unloading operations.</p>
              </div>
              <div className="advantage-item">
                <h4>⏰ 24/7 Availability</h4>
                <p>Round-the-clock service to meet your urgent transportation needs.</p>
              </div>
              <div className="advantage-item">
                <h4>💰 Competitive Pricing</h4>
                <p>Transparent and affordable rates for all our truck rental services.</p>
              </div>
              <div className="advantage-item">
                <h4>🏠 Comprehensive Services</h4>
                <p>Home shifting, office moving, and delivery services across Dubai.</p>
              </div>
              <div className="advantage-item">
                <h4>📞 Easy Booking</h4>
                <p>Simple booking process with instant confirmation and support.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="contact-cta">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Experience Our Services?</h2>
              <p>Contact us today for reliable pickup truck rental services in Dubai</p>
              <div className="cta-buttons">
                <a href={`tel:${CONTACT_INFO.phone}`} className="btn-primary">Call: {CONTACT_INFO.phone}</a>
                <a href={`mailto:${CONTACT_INFO.email}`} className="btn-secondary">Email Us</a>
              </div>
            </div>
          </div>
        </section>
      </div>
  );
};

export default About;
